import {
  artistLinkPageSongPlatformTypes,
  type ArtistLinkPageSongPlatformLink,
} from "@libs/link-in-bio-config";

/** Label of each platform in a song's links, as the public page shows it. */
export const SONG_PLATFORM_LINK_LABELS: Record<
  ArtistLinkPageSongPlatformLink["type"],
  string
> = {
  lissen: "Lissen",
  spotify: "Spotify",
  appleMusic: "Apple Music",
  deezer: "Deezer",
  youtubeMusic: "YouTube Music",
  tidal: "Tidal",
  boomplay: "Boomplay",
  audiomack: "Audiomack",
};

/**
 * Apply a song's page-owned platform urls (`platformLinkOverridesBySongId`)
 * to its source links, so the editor preview and the public page agree.
 *
 * An override replaces the url of a platform the source already has and adds
 * a platform it lacks. The Lissen link is generated and is never overridden.
 * The result keeps the canonical platform order the source links use.
 */
export function applyPlatformLinkOverrides(
  links: readonly ArtistLinkPageSongPlatformLink[],
  overrides:
    Partial<Record<ArtistLinkPageSongPlatformLink["type"], string>> | undefined,
): ArtistLinkPageSongPlatformLink[] {
  if (!overrides) return [...links];
  const byType = new Map(links.map((link) => [link.type, link]));
  for (const type of artistLinkPageSongPlatformTypes) {
    const url = overrides[type];
    if (!url || type === "lissen") continue;
    byType.set(type, { type, label: SONG_PLATFORM_LINK_LABELS[type], url });
  }
  return artistLinkPageSongPlatformTypes.flatMap((type) => {
    const link = byType.get(type);
    return link ? [link] : [];
  });
}

/** Page-owned links apply only to ordinary songs, on both public pages and previews. */
export function songPlatformLinks(
  song: {
    platformLinks: ArtistLinkPageSongPlatformLink[];
    musicAccessId?: string | null;
    preSave?: { releaseDate: string };
  },
  overrides: Parameters<typeof applyPlatformLinkOverrides>[1],
  order?: readonly ArtistLinkPageSongPlatformLink["type"][],
): ArtistLinkPageSongPlatformLink[] {
  return orderPlatformLinks(
    song.musicAccessId
      ? song.platformLinks.filter((link) => link.type === "lissen")
      : song.preSave
        ? song.platformLinks
        : applyPlatformLinkOverrides(song.platformLinks, overrides),
    order,
  );
}

/**
 * Resolve a song against its page-owned pre-save (`preSaveBySongId`), so the
 * editor preview and the public page agree.
 *
 * A released song (no `preSave` marker) is returned unchanged: its pre-save is
 * ignored from the release date. An unreleased song with a pre-save keeps its
 * identity and loses everything that plays or opens it: each platform the
 * pre-save offers links to the pre-save url, in canonical platform order. An
 * unreleased song without one is not shown (`null`).
 */
export function applySongPreSave<
  T extends {
    albumId: string | null;
    musicAccessId?: string | null;
    previewUrl: string | null;
    platformLinks: ArtistLinkPageSongPlatformLink[];
    preSave?: { releaseDate: string };
  },
>(
  song: T,
  preSave:
    | {
        url: string;
        platforms: readonly ArtistLinkPageSongPlatformLink["type"][];
      }
    | undefined,
): T | null {
  if (!song.preSave) return song;
  if (!preSave) return null;
  const platformLinks = preSavePlatformLinks(preSave);
  if (platformLinks.length === 0) return null;
  const { musicAccessId: _musicAccessId, ...rest } = song;
  return { ...rest, albumId: null, previewUrl: null, platformLinks } as T;
}

/** The platform links of a pre-save: each offered platform opens its url. */
function preSavePlatformLinks(preSave: {
  url: string;
  platforms: readonly ArtistLinkPageSongPlatformLink["type"][];
}): ArtistLinkPageSongPlatformLink[] {
  const offered = new Set(preSave.platforms);
  return artistLinkPageSongPlatformTypes.flatMap((type) =>
    type !== "lissen" && offered.has(type)
      ? [{ type, label: SONG_PLATFORM_LINK_LABELS[type], url: preSave.url }]
      : [],
  );
}

/** The calendar date (`YYYY-MM-DD`, UTC) pages compare a release date with. */
export function pageDateOf(now: Date): string {
  return now.toISOString().slice(0, 10);
}

/**
 * The song a pre-save track (a `preSaveBySongId` entry with `track`) is shown
 * as while no song with its ISRC resolves, so the editor preview and the
 * public page agree.
 *
 * It has no Lissen link, album, playback or preview: each platform the
 * pre-save offers opens the pre-save url. It carries the `preSave` marker
 * until `today` reaches its release date; after that it keeps the same links
 * without the marker. `null` when the pre-save offers no platform.
 */
export function preSaveTrackSong(input: {
  id: string;
  artistName: string;
  preSave: {
    url: string;
    platforms: readonly ArtistLinkPageSongPlatformLink["type"][];
    track: { title: string; artworkUrl: string | null; releaseDate: string };
  };
  today: string;
}) {
  const { track } = input.preSave;
  const platformLinks = preSavePlatformLinks(input.preSave);
  if (platformLinks.length === 0) return null;
  return {
    id: input.id,
    albumId: null,
    title: track.title,
    artistName: input.artistName,
    artworkUrl: track.artworkUrl,
    durationSeconds: 0,
    previewUrl: null,
    platformLinks,
    ...(track.releaseDate > input.today
      ? { preSave: { releaseDate: track.releaseDate } }
      : {}),
  };
}

/**
 * Reorder platform links per an item-level DSP order override
 * (`platformLinkOrderByItemId`), so a manual per-item override renders
 * identically on the editor preview and the public page.
 *
 * The Lissen link is always pinned first and cannot be displaced by a saved
 * override. The remaining links whose `type` appears in `platformLinkOrder`
 * are sorted by their rank in that override. Any remaining links (types NOT
 * present in the override) keep their original relative order and are
 * appended after.
 *
 * Shared by the editor preview builder (`@libs/lib-editor`) and the public
 * page assembler (`@libs/link-in-bio`) — previously duplicated verbatim in
 * both packages.
 */
export function orderPlatformLinks<
  T extends { type: ArtistLinkPageSongPlatformLink["type"] },
>(
  links: readonly T[],
  platformLinkOrder?: readonly ArtistLinkPageSongPlatformLink["type"][],
): T[] {
  const pinned = links.filter((link) => link.type === "lissen");
  const movable = links.filter((link) => link.type !== "lissen");
  if (!platformLinkOrder || platformLinkOrder.length === 0) {
    return [...pinned, ...movable];
  }
  const rank = new Map<ArtistLinkPageSongPlatformLink["type"], number>(
    platformLinkOrder
      .filter((type) => type !== "lissen")
      .map((type, index) => [type, index]),
  );
  const known: T[] = [];
  const rest: T[] = [];
  for (const link of movable) {
    (rank.has(link.type) ? known : rest).push(link);
  }
  known.sort((a, b) => rank.get(a.type)! - rank.get(b.type)!);
  return [...pinned, ...known, ...rest];
}

/**
 * Parse a release-date-like string into epoch ms for recency sorting.
 * Missing, empty, or unparsable values sort last (`0`).
 */
export function parseReleaseTimestamp(
  value: string | null | undefined,
): number {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

/**
 * Release time (epoch ms) of a song's primary album, used to order a Music
 * section's "recent" auto sort. `0` when there is no album or no release
 * date, which sorts the song last.
 *
 * Shared by `@libs/lib-editor`'s `build-preview.ts` and
 * `song-section-utils.ts` — previously a byte-identical local copy in each.
 */
export function releaseTimeOf(song: {
  album?: { releaseDate: string | null } | null;
}): number {
  return parseReleaseTimestamp(song.album?.releaseDate);
}
